/* ----------------------- Argument Card ----------------------- */
import React from "react";
import {ThoughtInSearchDTO} from "@/generated-client";
import {Card, CardContent, CardFooter, CardHeader, CardTitle} from "@/components/ui/card";
import {Bookmark, MessageSquareQuote, ArrowBigDown, ArrowBigUp} from "lucide-react";
import {Localizer} from "@/main/react/Types";
import DateFormatter from "@/main/common/DateFormatter";
import SafeImage from "@/main/util/SafeImage";
import {getDisplayUsername, getUserProfilePath} from "@/main/common/UsernameDisplayUtils";
import {CardMetadataBadges, type CardMetadataBadgeItem} from "@/main/react/card/CardMetadataBadges";
import { getSensitivePreferenceMode, resolveSensitiveMaskingConfig, type SensitiveMaskingDirective } from "@/main/common/SensitivePreference";
import SensitiveMediaGuard from "@/main/react/sensitive/SensitiveMediaGuard";
import SensitiveBadge from "@/main/react/sensitive/SensitiveBadge";
import {SMALL_THUMBNAIL_IMAGE_SIZE} from "@/main/util/ImageUrlHelper";
import { getFriendlyCountBadgeLabel } from "@/main/react/card/CardMetadataBadgeCopy";
import { getMaskedContentWarningCopy } from "@/main/react/sensitive/ContentWarningCopy";
import AuthorAttribution from "@/main/react/component/AuthorAttribution";
import { thoughtPermalinkHref } from "@/main/react/thoughts/thoughtChainRoute";
import { enhanceBodyImageFallbacks } from "@/main/react/discussion/bodyImagePermalinks";
import { stripHtml } from "@/main/react/page/search/searchUtils";
import { VoteContainer } from "@/main/react/buttons/CtaButton";
import { ClientFactory } from "@/main/common/ClientFactory";
import { isHandledAuthSessionError } from "@/main/common/AjaxAuthSessionHandler";
import { applyVoteToState, type DiscussionVoteState } from "@/main/common/DiscussionVoteState";
import { getViewerActionRecord, rememberViewerActionRecord, resolveViewerActionRecord } from "@/main/common/ViewerActionRecords";
import { ACCOUNT_ACCESS_DIALOG_COPY, withAccountAccessSurface } from "@/main/react/account_access/AccountAccessDialogContext";
import { trackThoughtVote } from "@/main/analytics/analyticsEvents";
import { toast } from "sonner";
import logger from "@/lib/logger";

/**
 * Cap on the plain-text excerpt that names the row's overlay link.
 *
 * A thought is arbitrary member rich text, so an uncapped accessible name is its own accessibility
 * defect — a screen reader would read an entire essay before the listener learns where the link goes.
 * `EMPTY-STATES-9`.
 */
const THOUGHT_LINK_LABEL_MAX_LENGTH = 120;

/**
 * A plain-text, length-capped excerpt of a thought body, for use as an accessible name.
 *
 * Built from the rendered HTML through the same `stripHtml` helper `SearchThoughtResultRow` already
 * uses to name the identical overlay-anchor pattern — never from the rendered node, which is arbitrary
 * member HTML. Clipped on a word boundary when one is available late enough to still carry meaning.
 */
export function buildThoughtLinkExcerpt(html?: string | null): string {
    const text = stripHtml(html);
    if (text.length <= THOUGHT_LINK_LABEL_MAX_LENGTH) {
        return text;
    }
    const clipped = text.slice(0, THOUGHT_LINK_LABEL_MAX_LENGTH);
    const lastSpace = clipped.lastIndexOf(" ");
    const kept = lastSpace > THOUGHT_LINK_LABEL_MAX_LENGTH / 3 ? clipped.slice(0, lastSpace) : clipped;
    return `${kept.trimEnd()}…`;
}

export function ThoughtCardDisplayer(props: {
    argument: ThoughtInSearchDTO;
    sensitiveMasking?: SensitiveMaskingDirective;
    /**
     * `false` drops the THOUGHT author's byline — the inner one, not the discussion author's.
     *
     * On "thoughts I wrote" that inner byline is the viewer on every row, sitting directly under the
     * discussion author's byline, so the card carried two attributions where one of them was
     * already implied by the route. One contract with DiscussionCardDisplayer and
     * SeriesCardDisplayer. Design doc §9.3.
     */
    showAuthor?: boolean;
    /**
     * Which half of the card is the headline.
     *
     * `"discussion"` (default) keeps the container discussion's title as the `text-lg` headline —
     * right in search, where you are looking for the discussion. `"thought"` promotes the user's own
     * words to the headline and demotes the discussion title to a context line above it, which is
     * right on a list OF thoughts: there the discussion title is the least distinguishing element on
     * the card, and it was rendering at roughly twice the size of the thing you came to read.
     */
    /**
     * `"reacted"` is the third value (`EMPTY-STATES-89` / `PROFILE-13`, wave-12 sheet Q21 option A,
     * 2026-08-24). It exists because the two moves `"thought"` makes were fused into one value and the
     * reacted dashboard lists need exactly one of them.
     *
     * <p>On a REACTED list every row is somebody else's thought inside somebody else's post, so the
     * discussion stays the headline and the thought's own byline has to survive — but the DISCUSSION's
     * creation date is still the least useful figure on the card, and printing it beside the thought's
     * own produced two bare `timeSince` values ~50px apart with no word saying which is which.
     * `"reacted"` therefore = `"discussion"` minus the discussion's date.
     */
    emphasis?: "discussion" | "thought" | "reacted";
    /**
     * `false` drops the upvote/downvote CONTROL and leaves the score as a static pair of counts.
     *
     * Operator decision D9 (2026-08-20): on a list of the viewer's own work the card stops offering
     * reader controls on writing the viewer produced. It shipped for discussions on 2026-08-21
     * (`DASHBOARD-25`, `DiscussionButtons`' `showViewerReactions`) and not for thoughts, so the same
     * dashboard answered "is this list mine" two different ways one pill apart (`DASHBOARD-50`).
     *
     * The counts STAY, which is not a fresh decision — it is the one the discussion half already
     * took and pinned: "an author wants to see the votes, they just cannot cast one"
     * (`DiscussionButtonsOwnerFooter.ux6.spec.tsx`). One prop, matching that surface's, rather than
     * four.
     *
     * ⚠️ Consumed at the RENDER SITES only. It must never move into the `showVoteControl` `useState`
     * initializer below — mount-captured state that disagrees with a live prop is the lifetime bug
     * rule 31's react-query corollary describes — nor into the `ViewerActionRecords` resync effect,
     * whose dependency list exists to bail out on content equality and has no business re-running
     * for something that is not a vote.
     */
    showViewerReactions?: boolean;
    localizer: Localizer;
}) {
    const {argument, sensitiveMasking, showAuthor = true, emphasis = "discussion", showViewerReactions = true, localizer} = props;
    const thoughtIsHeadline = emphasis === "thought";
    // Derived once, and deliberately NOT `!thoughtIsHeadline`: `"reacted"` keeps the discussion as the
    // headline and still drops its date, which is the whole reason the third value exists. Reading this
    // off `thoughtIsHeadline` is what fused the two moves together in the first place.
    const showDiscussionCreatedAt = emphasis === "discussion";
    const argumentWithFlags = argument as ThoughtInSearchDTO & {
        sensitive?: boolean | null;
        discussionSensitive?: boolean | null;
    };
    const df = new DateFormatter(localizer);

    // Always the thought's own permalink. The `/conversation/{cid}/thought/{tid}` shape this used to
    // prefer when `conversationPublicId` was set redirects to the bare discussion and discards the
    // thought entirely; the field is a fossil that no repository populates. Design §3 A2.
    //
    // Built by the guarded helper, never by a local template literal: the `?? ""` fallbacks this
    // replaced turned an absent slug or id into `/discussion//thought/` — well-formed, clickable and
    // permanently 404ing. `null` means "no navigation", and the overlay link below is omitted rather
    // than pointed somewhere broken. Five-fixes design, Fix 4.
    const link = thoughtPermalinkHref(argument?.discussionSlug, argument?.publicId);


    const discussionImageUrl = argument?.discussionImageUrl ?? "";
    const discussionTitle = argument?.discussionTitle ?? "";
    const discussionAuthorUsername = argument?.discussionAuthorUsername ?? "";
    const displayDiscussionAuthorUsername = getDisplayUsername(discussionAuthorUsername, localizer);
    const displayArgumentUsername = getDisplayUsername(argument?.username ?? "", localizer);
    const discussionAuthorProfilePath = getUserProfilePath(discussionAuthorUsername, "discussions");
    const argumentAuthorProfilePath = getUserProfilePath(argument?.username ?? "", "discussions");
    const argumentCreatedAtLabel = argument?.createdAt ? df.timeSince(argument.createdAt) : "";
    const htmlContent = argument?.htmlContent ?? "";
    const agreementsCount = Number(argument?.agreementsCount ?? 0);
    const disagreementsCount = Number(argument?.disagreementsCount ?? 0);
    const totalVotesCount = agreementsCount + disagreementsCount;
    const bookmarksCount = Number(argument?.argumentBookmarksCount ?? 0);
    const isSensitive = Boolean(argumentWithFlags.discussionSensitive ?? argumentWithFlags.sensitive);
    const { sensitivePreference, hideSensitiveMedia } = resolveSensitiveMaskingConfig(getSensitivePreferenceMode(), sensitiveMasking);
    const warningCopy = React.useMemo(
        () => getMaskedContentWarningCopy(localizer, { isSensitive }),
        [isSensitive, localizer],
    );

    const displayTitle = discussionTitle || displayDiscussionAuthorUsername;
    const discussionCreatedAtLabel = argument?.discussionCreatedAt ? df.timeSince(argument.discussionCreatedAt) : "";

    // ------------------------------------------------------------------------------------------
    // The viewer's own vote, and its inverse (`EMPTY-STATES-10`).
    //
    // `/my_dashboard/arguments/agreed` and `/disagreed` are lists whose ENTIRE membership criterion is
    // a vote the viewer cast, and the card showed neither the vote nor any way to withdraw it:
    // withdrawing meant opening the permalink, finding the thought inside the discussion, voting
    // there, and coming back to a list whose scroll position was gone.
    //
    // It self-wires from the DTO rather than from a page prop: `viewer_agreement_mode` is already
    // projected by `UserCollectionPaginationRepository.USER_ARGUMENT_SELECT_TEMPLATE` for every
    // dashboard and profile thought list, so the number already exists and no call site has to thread
    // it. A search row, where the viewer has not voted, grows no control.
    // ------------------------------------------------------------------------------------------
    const thoughtPublicId = argument?.publicId ?? "";
    const viewerAgreeingFromDto = argument?.viewerAgreementMode === "UPVOTE";
    const viewerDisagreeingFromDto = argument?.viewerAgreementMode === "DOWNVOTE";

    // Rule 33 / `ViewerActionRecords`: `PaginatedListWrapperV2` banks a list's `items` BY REFERENCE
    // into `NavigationListStateCache` and the restored list outranks fresh server data, so a vote kept
    // only in `useState` is re-seeded pre-action on Back — the 2026-08-09 feed-upvote defect, same
    // shape, same card family. The control seeds FROM the registry and never writes its props back
    // into it.
    const [voteState, setVoteState] = React.useState<DiscussionVoteState>(() => resolveViewerActionRecord(
        "thought-vote",
        thoughtPublicId,
        {
            agreementsCount,
            disagreementsCount,
            viewerAgreeing: viewerAgreeingFromDto,
            viewerDisagreeing: viewerDisagreeingFromDto,
        },
    ));
    const [isTogglingVote, setIsTogglingVote] = React.useState<boolean>(false);

    // "Has vote context here", NOT "currently holds a vote" — captured once at mount. Gating on the
    // live vote would make the control delete itself the moment you used it, which is a worse defect
    // than the one being fixed. A registry entry counts as context on its own, so the control survives
    // a Back restoration that re-seeds the row from a stale DTO.
    const [showVoteControl] = React.useState<boolean>(() => Boolean(thoughtPublicId) && (
        viewerAgreeingFromDto
        || viewerDisagreeingFromDto
        || getViewerActionRecord("thought-vote", thoughtPublicId) !== undefined
    ));

    // `PROFILE-21` — the body container, so the fallback pass has something to walk. See the
    // comment at the element itself for why this is the enhancement and not `useBodyMediaLightbox`.
    const bodyRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        // The label is the already-shipped `discussion.body.image_unavailable` (25/25 catalogs since
        // wave 6), passed from here because `bodyImagePermalinks` enhances injected DOM and has no
        // React context of its own — the same reason `useBodyMediaLightbox` passes it.
        enhanceBodyImageFallbacks(bodyRef.current, localizer("discussion.body.image_unavailable"));
    });

    // `DASHBOARD-50` — the owner-context gate, derived at RENDER TIME from the prop and the
    // mount-captured "has vote context here" state. Two plain consts over an unchanged `useState`
    // and an unchanged effect: nothing new is stored, nothing new is published, and no dependency
    // list grew (rule 31).
    const voteControlVisible = showVoteControl && showViewerReactions;
    const ownWorkStaticVotesVisible = showVoteControl && !showViewerReactions;

    // Re-seeds from `record ?? props`, exactly as the mount does. The functional setter returns the
    // CURRENT object when the content is unchanged so React bails out: the seed is reconstructed on
    // every pass, and an identity-compared resync of a freshly built object is React #185 arrived at
    // from outside a `useSyncExternalStore` store (rule 31's discipline, one module over).
    React.useEffect(() => {
        const next = resolveViewerActionRecord("thought-vote", thoughtPublicId, {
            agreementsCount,
            disagreementsCount,
            viewerAgreeing: viewerAgreeingFromDto,
            viewerDisagreeing: viewerDisagreeingFromDto,
        });
        setVoteState((current) => (
            current.agreementsCount === next.agreementsCount
            && current.disagreementsCount === next.disagreementsCount
            && current.viewerAgreeing === next.viewerAgreeing
            && current.viewerDisagreeing === next.viewerDisagreeing
        ) ? current : next);
    }, [thoughtPublicId, agreementsCount, disagreementsCount, viewerAgreeingFromDto, viewerDisagreeingFromDto]);

    const toggleVote = async (mode: "UPVOTE" | "DOWNVOTE"): Promise<void> => {
        if (isTogglingVote || !thoughtPublicId) {
            return;
        }
        setIsTogglingVote(true);
        const previousVoteState = voteState;
        const currentlyActive = mode === "UPVOTE" ? previousVoteState.viewerAgreeing : previousVoteState.viewerDisagreeing;
        // The same toggle arithmetic every other vote control uses, from the same function.
        const optimisticState = applyVoteToState(previousVoteState, mode === "UPVOTE" ? "AGREE" : "DISAGREE");
        setVoteState(optimisticState);

        try {
            const apiClient = ClientFactory.getAjaxThoughtRestControllerApi();
            if (currentlyActive) {
                await apiClient.removeVoteFromThought(thoughtPublicId);
            } else {
                await apiClient.voteOnThought(thoughtPublicId, mode);
                trackThoughtVote(mode === "UPVOTE" ? "upvote" : "downvote");
            }
            // Recorded only once the write has SETTLED, so the record never holds a vote the server
            // refused. This endpoint returns no state body, so the optimistic prediction is the best
            // truth available.
            rememberViewerActionRecord("thought-vote", thoughtPublicId, optimisticState);
        } catch (e) {
            setVoteState(previousVoteState);
            // The revert is recorded too: leaving a record of the failed vote in place would make the
            // next remount render it, which is the defect the registry exists to prevent, inverted.
            rememberViewerActionRecord("thought-vote", thoughtPublicId, previousVoteState);
            if (isHandledAuthSessionError(e)) {
                return;
            }
            logger.error(e);
            toast.error(localizer("typescript.thought.vote.update_failed"));
        } finally {
            setIsTogglingVote(false);
        }
    };

    // The overlay anchor's accessible name (`EMPTY-STATES-9`). It used to be `displayTitle` — the
    // PARENT DISCUSSION's title — while the href is the THOUGHT's permalink, so on a tab with no
    // grouping several consecutive rows announced identically with different destinations and the name
    // never once contained the thing that distinguishes them. WCAG 2.4.4.
    //
    // Built from a parameterized catalog key, never by joining fragments in code: half the catalogs
    // need a different order or different punctuation, and a concatenation cannot express that.
    const thoughtExcerpt = buildThoughtLinkExcerpt(htmlContent);
    const cardLinkLabel = thoughtExcerpt
        ? (displayTitle ? localizer("thought.card.link.aria", thoughtExcerpt, displayTitle) : thoughtExcerpt)
        : displayTitle;

    const engagementItems: CardMetadataBadgeItem[] = [];

    // The total is only worth its own badge when the split is not already the whole story. With one
    // upvote and no downvotes the card printed "1 vote" and "1 upvote" side by side — two badges
    // carrying one number, on every row of a list where that is the common case. Showing the total
    // only when BOTH directions have votes keeps it exactly where it adds something: as the sum a
    // reader would otherwise have to do.
    const votesAreSplit = agreementsCount > 0 && disagreementsCount > 0;

    // Suppressed entirely while the live vote control is showing. An inert "1 upvote" caption beside a
    // control displaying the same number, on a list defined BY that upvote, is the duplication
    // `EMPTY-STATES-10` objects to restated — and the caption is the half that cannot say the vote was
    // yours. The bookmark badge is unaffected: no control on this card replaces it.
    const showCountBadges = !showVoteControl;

    if (showCountBadges && totalVotesCount > 0 && votesAreSplit) {
        engagementItems.push({
            key: "votes",
            icon: <MessageSquareQuote className="size-3.5" aria-hidden={true}/>,
            label: getFriendlyCountBadgeLabel(localizer, "badge.thought.votes", totalVotesCount) ?? undefined,
        });
    }

    if (showCountBadges && agreementsCount > 0) {
        engagementItems.push({
            key: "agreements",
            icon: <ArrowBigUp className="size-3.5" aria-hidden={true}/>,
            label: getFriendlyCountBadgeLabel(localizer, "badge.thought.upvotes", agreementsCount) ?? undefined,
        });
    }

    if (showCountBadges && disagreementsCount > 0) {
        engagementItems.push({
            key: "disagreements",
            icon: <ArrowBigDown className="size-3.5" aria-hidden={true}/>,
            label: getFriendlyCountBadgeLabel(localizer, "badge.thought.downvotes", disagreementsCount) ?? undefined,
        });
    }

    if (bookmarksCount > 0) {
        engagementItems.push({
            key: "bookmarks",
            icon: <Bookmark className="size-3.5" aria-hidden={true}/>,
            label: getFriendlyCountBadgeLabel(localizer, "badge.thought.bookmarks", bookmarksCount) ?? undefined,
        });
    }

    return (
        // No vertical margin: the list shell (MainPageListStack) is `flex flex-col gap-2` and flex gaps
        // do not collapse margins, so `my-4` put 16+8+16 = 40px between rows where the sibling
        // DiscussionCardDisplayer in the same stack sits at 8px. Spacing belongs to the stack.
        <Card
            className="relative w-full max-w-full overflow-hidden rounded-xl border border-border py-0 gap-0 card-hover"
        >
            <SensitiveMediaGuard
                isSensitive={isSensitive}
                hideSensitiveMedia={hideSensitiveMedia}
                sensitivePreference={sensitivePreference}
                variant="card"
                // The full-card link below is a sibling of this guard, so without this the masked scrim
                // covers it and a blurred card cannot be opened without revealing first. Design §8.6.
                overlayStacking="below-link-overlay"
                // A masked card keeps its children in flow and only blurs them, so it reserved the FULL
                // height of the content it is hiding — 410 CSS px of blur carrying no title, author,
                // date or counts, 45% of a phone viewport for zero information. Capped below `sm` only:
                // the reveal control is on an `absolute inset-0` layer, so it stays centred in whatever
                // height this leaves, and the cap lifts the moment the card is revealed.
                maskedContentClassName="max-h-48 sm:max-h-none"
                badgeLabel={warningCopy.badgeLabel}
                label={warningCopy.label}
                helperText={warningCopy.helperText}
                revealText={warningCopy.revealText}
                // The HIDE branch has no reveal control by design, so without this a masked card is a
                // wall: nothing to press, and no way to reach the preference that produced it. An
                // anonymous visitor is served `content="HIDE"`, so that wall was the DEFAULT
                // logged-out experience of a sensitive card. The guard renders this only under HIDE
                // (`!allowsLocalReveal`), so a blurred card is unchanged. `GROUP-1`, 2026-08-14.
                preferencesRouteText={warningCopy.preferencesRouteText}
            >
                <CardHeader className="gap-2 px-3 py-3 sm:px-4 sm:py-4">
                    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start">
                        <div className="hidden shrink-0 sm:block">
                            {discussionImageUrl ? (
                                <SafeImage
                                    src={discussionImageUrl}
                                    alt={displayTitle}
                                    seavienImageSize={SMALL_THUMBNAIL_IMAGE_SIZE}
                                    wrapperClassName="h-16 w-20 overflow-hidden rounded-xl border border-border/60 bg-background2"
                                    className="h-full w-full object-cover discussion-thumb"
                                    onError={() => {
                                    }}
                                />
                            ) :null}
                        </div>

                        <div className="min-w-0 flex-1">


                            {/* Under `emphasis="thought"` this stops being the headline and becomes the
                                context line: same element, same link target, demoted type. The card
                                still says which discussion the thought lives in — it just stops
                                saying it twice the size of the thought itself. */}
                            {/* `text-neutral-text`, not `text-neutral` (`DASHBOARD-3`). `--neutral` is
                                a FILL token: gray-400 in light, measuring 2.40:1 against this card,
                                and `00-variables.css` already names it "the pair to avoid rather than
                                the pair to reach for". `--neutral-text` is the AA-safe twin declared
                                in both themes for exactly this. */}
                            {/* `headingLevel={3}` — `EMPTY-STATES-83`, 2026-08-23. `CardTitle` is a
                                plain `div` unless a host asks for the element, and until this row
                                only `DiscussionCardDisplayer` did, so a list of thought cards
                                contributed no headings at all.

                                Under `emphasis="thought"` this element is the DEMOTED context line
                                rather than the visual headline, and it is still the right heading
                                text: it is the row's only short, stable, distinguishing label,
                                while the body below it is an arbitrary-length member paragraph that
                                would make a terrible heading. One card family, one contract, in
                                both modes — asserted for both in the spec so the two cannot drift. */}
                            <CardTitle
                                headingLevel={3}
                                className={thoughtIsHeadline
                                    ? "line-clamp-1 text-xs font-medium leading-tight text-neutral-text sm:text-sm"
                                    : "line-clamp-2 text-lg font-semibold leading-tight text-text-main sm:text-xl"}
                            >
                                <span className="inline-flex flex-wrap items-center gap-2">
                                    <span>{displayTitle}</span>
                                    {isSensitive ? <SensitiveBadge className="py-0">{localizer("content-warning.badge.sensitive")}</SensitiveBadge> : null}
                                </span>
                            </CardTitle>

                            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-neutral-text sm:text-sm">
                                {/* Under `emphasis="thought"` AND `emphasis="reacted"` the DISCUSSION's
                                    creation date is dropped (`DASHBOARD-15`, then `EMPTY-STATES-89`). The card was rendering two bare
                                    `timeSince` figures about 50px apart — the discussion's age here
                                    and the thought's own below — both small, both grey, neither
                                    carrying a word saying which is which, so when they differed the
                                    card read as a mistake. On a list OF thoughts the discussion's age
                                    is the least useful fact on the card, and dropping it makes the
                                    surviving figure unambiguously the answer to "when was this
                                    written". Under the default emphasis (search, where the discussion
                                    IS the subject) nothing changes. */}
                                <AuthorAttribution localizer={localizer}
                                    name={displayDiscussionAuthorUsername}
                                    href={discussionAuthorProfilePath}
                                    imageUrl={argument.discussionAuthorProfileImageUrl}
                                    avatarSeed={discussionAuthorUsername || displayDiscussionAuthorUsername}
                                    details={showDiscussionCreatedAt ? (discussionCreatedAtLabel || null) : null}
                                    className="text-xs text-neutral-text sm:text-sm"
                                />
                            </div>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className="px-3 py-0 pb-3 sm:px-4 sm:pb-4">
                    <div className="min-w-0">
                        {/* Normal case. Spaced capitals made the thought author ~40% wider than the same
                            name rendered normally 40px above it in the same card, and read as a label
                            rather than a username at phone size. The leading quote icon is what
                            distinguishes this row. */}
                        {/* `text-neutral-text` with NO opacity multiplier (`DASHBOARD-3`). The
                            `text-neutral/80` this replaces measured 1.97:1 in light: an opacity
                            applied on top of an already-muted fill token is how a 2.4:1 pair became a
                            2.0:1 one. */}
                        <div className="mb-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-medium text-neutral-text">
                            <MessageSquareQuote className="size-3.5" aria-hidden={true}/>
                            {showAuthor ? (
                                <AuthorAttribution localizer={localizer}
                                    name={displayArgumentUsername}
                                    href={argumentAuthorProfilePath}
                                    imageUrl={argument.profileImageUrl}
                                    avatarSeed={argument?.username ?? displayArgumentUsername}
                                    details={argumentCreatedAtLabel || null}
                                    className="min-w-0 text-xs font-medium normal-case tracking-normal text-neutral-text"
                                    avatarClassName="size-5"
                                />
                            ) : (
                                // The timestamp is the half worth keeping on a self-list: "when did I
                                // write this" is the question, and the answer to "who wrote it" is the
                                // page you are already on.
                                argumentCreatedAtLabel ? (
                                    <span className="min-w-0 normal-case tracking-normal">{argumentCreatedAtLabel}</span>
                                ) : null
                            )}
                        </div>

                        {/* `[&_a]:relative [&_a]:z-layer-raised-front` lifts the body's own links —
                            @mentions above all — above the full-card overlay anchor (`DASHBOARD-10`).
                            The body is injected with `dangerouslySetInnerHTML` into a statically
                            positioned container, and a non-positioned inline anchor paints BELOW a
                            positioned sibling carrying a z-index, so the overlay won hit-testing over
                            the whole body: a visible, underlined, link-styled `@name` navigated to the
                            thought under the pointer while Enter followed the real href. Pointer and
                            keyboard did different things on the same visible link, and only the
                            pointer user got the wrong one. Same convention `AuthorAttribution` already
                            applies to its own two anchors. */}
                        {/* `PROFILE-21` — a body image whose asset is gone renders the BROWSER's
                            broken-image glyph inside a product-owned, bordered, muted-filled box.
                            The container is ours and the failure state was not. `DISCUSSION-7`
                            already built the answer: `enhanceBodyImageFallbacks` hides the dead
                            `<img>` and inserts a labelled sibling placeholder, with the label
                            written as `textContent` so a catalog value can never inject markup into
                            a member's body.

                            Deliberately the ENHANCEMENT, not `useBodyMediaLightbox`. That hook also
                            wraps every body image in a permalink anchor and mounts a media viewer
                            behind a history guard — on a `line-clamp-4` card sitting under a
                            full-card overlay anchor that would stop a tap navigating to the thought
                            and start opening a lightbox, which is a product decision this row did
                            not take. Its two existing consumers (`DiscussionContentDisplayer`,
                            `ThoughtDisplayerInDiscussion`) are full body surfaces; a feed card is
                            not.

                            No dependency array, for the hook's own recorded reason: React re-applies
                            `dangerouslySetInnerHTML` whenever the host re-renders with a fresh
                            `{__html}` object, which destroys the previous pass's work. The function
                            is idempotent by construction and tested to be. */}
                        <div
                            ref={bodyRef}
                            className={thoughtIsHeadline
                                ? "tiptap argument-rich-content argument-rich-content--compact min-w-0 bg-transparent! line-clamp-4 whitespace-pre-wrap text-base font-medium leading-6 text-text-main sm:text-lg sm:leading-7 [&_a]:relative [&_a]:z-layer-raised-front"
                                : "tiptap argument-rich-content argument-rich-content--compact min-w-0 bg-transparent! line-clamp-4 whitespace-pre-wrap text-sm leading-6 text-foreground [&_a]:relative [&_a]:z-layer-raised-front"}
                            dangerouslySetInnerHTML={{__html: htmlContent}}
                        />
                    </div>
                </CardContent>

                {engagementItems.length > 0 || showVoteControl ? (
                    /* Unchanged: the footer renders either the vote CONTROL or the static counts, so
                       `showVoteControl` still decides whether the row exists at all (`DASHBOARD-50`). */
                    <CardFooter className="flex flex-wrap items-center justify-between gap-3 px-3 pb-4 pt-0 sm:px-4">
                        {engagementItems.length > 0 ? (
                            <CardMetadataBadges
                                className="gap-2"
                                badgeClassName="border-border/50 bg-background2/60"
                                items={engagementItems}
                            />
                        ) : null}

                        {/* The inverse of the action that put this row in the list (`EMPTY-STATES-10`).
                            `relative z-layer-raised-front` is not decoration: the full-card overlay
                            anchor below sits at `z-layer-base-front`, so without the lift this control
                            renders perfectly and is unclickable. */}
                        {ownWorkStaticVotesVisible ? (
                            /* `DASHBOARD-50` — the viewer's own thought. The control goes, the score
                               stays: the same static presentation, the same reasoning and a distinct
                               hook, exactly as `DiscussionButtons` does it for the discussion half
                               ("KEEPS the counts — an author wants to see the votes, they just
                               cannot cast one"). No `z-layer-raised-front` lift, because static text
                               under the overlay anchor is not something anyone needs to click. */
                            <span
                                className="ml-auto flex items-center gap-3 text-sm text-muted-foreground"
                                data-playwright-id="thought-card-own-work-static-votes"
                            >
                                <span className="inline-flex items-center gap-1">
                                    <ArrowBigUp className="size-4" aria-hidden={true}/>
                                    <span aria-label={`${localizer("typescript.thought.upvote")}: ${voteState.agreementsCount}`}>
                                        {voteState.agreementsCount}
                                    </span>
                                </span>
                                <span className="inline-flex items-center gap-1">
                                    <ArrowBigDown className="size-4" aria-hidden={true}/>
                                    <span aria-label={`${localizer("typescript.thought.downvote")}: ${voteState.disagreementsCount}`}>
                                        {voteState.disagreementsCount}
                                    </span>
                                </span>
                            </span>
                        ) : null}

                        {voteControlVisible ? (
                            <div
                                className="relative z-layer-raised-front ml-auto"
                                data-playwright-id="thought-card-vote"
                            >
                                <VoteContainer
                                    leftButtonIcon={ArrowBigUp}
                                    leftButtonTitle={localizer("typescript.thought.upvote")}
                                    leftButtonCount={voteState.agreementsCount}
                                    leftButtonActive={voteState.viewerAgreeing}
                                    leftButtonExtraClass="upvote-thought-button"
                                    leftInteractionEffect="agree"
                                    leftCountFeedback="pulse"
                                    rightButtonIcon={ArrowBigDown}
                                    rightButtonTitle={localizer("typescript.thought.downvote")}
                                    rightButtonCount={voteState.disagreementsCount}
                                    rightButtonActive={voteState.viewerDisagreeing}
                                    rightButtonExtraClass="downvote-thought-button"
                                    rightInteractionEffect="disagree"
                                    rightCountFeedback="pulse"
                                    requiresLogin={true}
                                    accountAccessDialog={withAccountAccessSurface(ACCOUNT_ACCESS_DIALOG_COPY.react, "argument_react")}
                                    disabled={isTogglingVote}
                                    localizer={localizer}
                                    displayTextOnMobile={false}
                                    size="sm"
                                    onLeftClick={() => toggleVote("UPVOTE")}
                                    onRightClick={() => toggleVote("DOWNVOTE")}
                                />
                            </div>
                        ) : null}
                    </CardFooter>
                ) : null}
            </SensitiveMediaGuard>

            {link !== null ? (
                // `focus-visible` treatment with a NEGATIVE outline offset (`EMPTY-STATES-41`). This
                // anchor is the only way to open the row with a keyboard and carried no focus
                // treatment at all, while the visually identical search rows carry the app's token
                // ring. The negative offset is the load-bearing half: the card root is
                // `overflow-hidden` and an outline at offset 0 paints outside the border box, where it
                // can be clipped away entirely.
                <a
                    href={link}
                    className="absolute inset-0 z-layer-base-front rounded-xl focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-ring"
                    aria-label={cardLinkLabel}
                ></a>
            ) : null}
        </Card>
    );
}
